import React, { Component } from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../../../../store";
import { bindActionCreators, Dispatch } from "redux";
import BootstrapTable, {
  SelectRowProps,
  SortOrder,
  TableChangeState,
  TableChangeType,
} from "react-bootstrap-table-next";
import { Button, Form, InputGroup, Modal, Overlay, OverlayTrigger, Spinner, Tooltip, Badge } from "react-bootstrap";
import {
  addAssessmentToDataPeriod,
  getEvidenceAssessments,
  hideAddAssessmentModal,
  hideEnterDataManuallyModal,
  hidePickDataSourceModal,
  openAddAssessmentModal,
} from "../../../../../store/onboarding/actions";
import LoadingIndicator from "../../LoadingIndicator";
import {
  EvidenceAssessment,
  ManuallyAssessment,
} from "../../../../../store/onboarding/types";
import { toastr } from "react-redux-toastr";
import { GoalTypes } from "../../../../../store/onboarding/cases/types";
import AssessmentSuggestionsModal from "./AssessmentSuggestionsModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faEye, faPlusCircle, faPen } from "@fortawesome/free-solid-svg-icons";
import { FormattedMessage } from "react-intl";
import { width } from "@fortawesome/free-solid-svg-icons/faCaretUp";
import { UserInfo } from "../../../../../store/auth/types";

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

const columns = [
  {
    dataField: "id",
    text: "Assessment Id",
    hidden: true,
  },
  {
    dataField: "name",
    text: "Assessment Name",
    sort: true,
    headerStyle: { whiteSpace: "nowrap" },
  },
  {
    dataField: "is_system_assessment",
    text: "Type",
    sort: true,
    headerStyle: { whiteSpace: "nowrap" },
    formatter: (_: any, row: any) => {
    return row.is_system_assessment ? (
      <Badge variant="primary">System</Badge>
    ) : (
      <Badge variant="secondary">Custom</Badge>
    );
  },
  },
  {
    dataField: "use_grade_specific_color_criteria",
    text: "Grade Specific Color Criteria",
    sort: true,
    headerStyle: { whiteSpace: "nowrap" },
    formatter: (_: any, row: any) => {
      return <div className="text-center">
        <Form.Check
          type="checkbox"
          checked={row.use_grade_specific_color_criteria}
          disabled
          custom
        />
      </div>
    },
  },
  {
    dataField: "with_reading_algorithm",
    text: "Reading Algorithm",
    sort: true,
    headerStyle: { whiteSpace: "nowrap" },
    formatter: (_: any, row: any) => {
      return <div className="text-center">
        <Form.Check
          type="checkbox"
          checked={row.with_reading_algorithm}
          disabled
          custom
        />
      </div>
    },
  },
  {
    dataField: "dataColumns",
    text: "Column Name(s)",
    sort: true,
  },
  {
    dataField: "createdBy",
    text: "Created By",
    sort: true,
    headerStyle: { whiteSpace: "nowrap", width: "15%" },
  },
  {
    dataField: "actions",
    text: "Actions",
    headerStyle: { textAlign: "center" as const },
    style: { textAlign: "center" as const },
    formatter: (_: any, row: any) => row.actionsElement,
  },
];

type PropsFromState = {
  showModal: boolean;
  enterDataManuallyType?: GoalTypes;
  evidenceAssessments: Array<EvidenceAssessment>;
  currentUser: UserInfo;
  isLoading: {
    getEvidenceAssessments: boolean;
    addAssessmentToDataPeriod: boolean;
    createAssessment: boolean;
    updateAssessment: boolean;
  };
  errors: {
    getEvidenceAssessments?: string;
    addAssessmentToDataPeriod?: string;
    createAssessment?: string;
    updateAssessment?: string;
  };
};

type DispatchProps = {
  hideAddAssessmentModal: () => any;
  getEvidenceAssessments: (type?: GoalTypes) => any;
  addAssessmentToDataPeriod: (assessment: ManuallyAssessment) => any;
  hideEnterDataManuallyModal: () => any;
  hidePickDataSourceModal: () => any;
  openAddAssessmentModal: (isNewProgressMonitoring?: boolean, assessment?: EvidenceAssessment, mode?: 'create' | 'edit' | 'view', fromPage?: string) => any;
};

type Props = PropsFromState & DispatchProps;

type State = {
  assessmentsFilter: string;
  selectedAssessmentId?: number;
  evidenceAssessments: Array<EvidenceAssessment>;
  showSuggestionModal: boolean;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
};

const getCreatedByLabel = (assessment: EvidenceAssessment): string => {
  if ((assessment as any).is_system_assessment) {
    return "ReadyCoach";
  }

  const owner = (assessment as any).owner || {};
  const firstName = owner.first_name || (assessment as any).created_by_first_name || "";
  const lastName = owner.last_name || (assessment as any).created_by_last_name || "";
  const nameFromParts = `${firstName} ${lastName}`.trim();

  if (nameFromParts) {
    return nameFromParts;
  }

  const fallbackName = owner.name || (assessment as any).created_by_name;
  return fallbackName || "-";
};



class EnterDataManuallyModal extends Component<Props, State> {
  state: Readonly<State> = {
    evidenceAssessments: [],
    assessmentsFilter: "",
    selectedAssessmentId: undefined,
    showSuggestionModal: false,
    sortField: undefined,
    sortOrder: undefined,
  };

  componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: Readonly<State>
  ): void {
    if (
      prevProps.isLoading.getEvidenceAssessments &&
      !this.props.isLoading.getEvidenceAssessments &&
      !this.props.errors.getEvidenceAssessments
    ) {
      this.setState({ evidenceAssessments: this.props.evidenceAssessments });
    }
    if (
      prevProps.isLoading.addAssessmentToDataPeriod &&
      !this.props.isLoading.addAssessmentToDataPeriod
    ) {
      if (this.props.errors.addAssessmentToDataPeriod) {
        toastr.error("Error", this.props.errors.addAssessmentToDataPeriod);
      } else {
        this.props.hidePickDataSourceModal();
      }
    }

    if (
      prevProps.isLoading.updateAssessment &&
      !this.props.isLoading.updateAssessment
    ) {
      if (this.props.errors.updateAssessment) {
        toastr.error("Failed", this.props.errors.updateAssessment);
      } else {
        toastr.success("Success", "Assessment updated successfully");
        this.props.hideAddAssessmentModal();
      }
    }

    if (
      prevProps.isLoading.createAssessment &&
      !this.props.isLoading.createAssessment
    ) {
      if (this.props.errors.createAssessment) {
        toastr.error("Failed", this.props.errors.createAssessment);
      } else {
        toastr.success("Success", "Assessment created successfully");
        this.props.hideAddAssessmentModal();
      }
    }
  }

  handleModalShow = () => {
    this.setState({
      assessmentsFilter: "",
      selectedAssessmentId: undefined,
      sortField: undefined,
      sortOrder: undefined,
    }, () => this.props.getEvidenceAssessments(this.props.enterDataManuallyType));
  };

  handleOnSelect = (row: any, isSelect: boolean) => {
    if (isSelect) {
      this.setState({
        selectedAssessmentId: row.id,
      });
    }
  };

  handleAssessmentsFilterChange = (event: React.ChangeEvent<any>) => {
    event.preventDefault();
    const { value } = event.target as HTMLInputElement;
    this.setState({
      assessmentsFilter: value,
      evidenceAssessments: this.props.evidenceAssessments.filter(
        (evidenceAssessment) =>
          evidenceAssessment.name
            .toLowerCase()
            .includes(value.trim().toLowerCase())
      ),
    });
  };

  handleAddAssessment = () => {
    const { selectedAssessmentId } = this.state;
    if (selectedAssessmentId) {
      const evidenceAssessment = this.props.evidenceAssessments.find(
        (evidenceAssessment) => evidenceAssessment.id === selectedAssessmentId
      );
      if (!evidenceAssessment) {
        return;
      }
      const manuallyAssessment: ManuallyAssessment = {
        assessment_id: selectedAssessmentId,
        measurement_ids: evidenceAssessment.measurements.map(
          (measurement) => measurement.id!
        ),
      };
      this.props.addAssessmentToDataPeriod(manuallyAssessment);
    }
  };

  handleModalHide = () => {
    if (
      !this.props.isLoading.getEvidenceAssessments &&
      !this.props.isLoading.addAssessmentToDataPeriod
    ) {
      setTimeout(() => {
        this.props.hideEnterDataManuallyModal();
      }, 1);
      this.setState({ showSuggestionModal: false });
    }
  };

  handleTableChange = (type: TableChangeType, { sortField: clickedField }: TableChangeState<any>) => {
    if (type === 'sort') {
      let newSortField: string | undefined = clickedField;
      let newSortOrder: 'asc' | 'desc' | undefined = 'asc';
  
      if (this.state.sortField === clickedField) {
        if (this.state.sortOrder === 'asc') {
          newSortOrder = 'desc';
        } else if (this.state.sortOrder === 'desc') {
          // Third click → Clear the sort
          newSortField = undefined;
          newSortOrder = undefined;
        }
      }
  
      this.setState({
        sortField: newSortField,
        sortOrder: newSortOrder,
      });
    }
  };

  getSortedEvidenceAssessments = (assessments: Array<EvidenceAssessment>) => {
    const { sortField, sortOrder } = this.state;

    if (!sortField || !sortOrder) {
      return assessments;
    }

    const sorted = [...assessments];
    sorted.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case "name":
          aValue = a.name;
          bValue = b.name;
          break;
        case "is_system_assessment":
          aValue = a.is_system_assessment ? "System" : "Custom";
          bValue = b.is_system_assessment ? "System" : "Custom";
          break;
        case "use_grade_specific_color_criteria":
          aValue = a.use_grade_specific_color_criteria ? 1 : 0;
          bValue = b.use_grade_specific_color_criteria ? 1 : 0;
          break;
        case "with_reading_algorithm":
          aValue = a.with_reading_algorithm ? 1 : 0;
          bValue = b.with_reading_algorithm ? 1 : 0;
          break;
        case "dataColumns":
          aValue = a.measurements.map((m) => m.display_name).join(", ");
          bValue = b.measurements.map((m) => m.display_name).join(", ");
          break;
        case "createdBy":
          aValue = getCreatedByLabel(a);
          bValue = getCreatedByLabel(b);
          break;
        default:
          return 0;
      }

      const isEmpty = (val: any) => val === null || val === undefined || val === "";

      if (isEmpty(aValue) && !isEmpty(bValue)) return 1;
      if (!isEmpty(aValue) && isEmpty(bValue)) return -1;
      if (isEmpty(aValue) && isEmpty(bValue)) return 0;

      if (typeof aValue === "string" || typeof bValue === "string") {
        const aText = (aValue ?? "").toString().trim();
        const bText = (bValue ?? "").toString().trim();
        return sortOrder === "asc"
          ? collator.compare(aText, bText)
          : collator.compare(bText, aText);
      }

      return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
    });

    return sorted;
  };

  render() {
    const selectRow: SelectRowProps<any> = {
      mode: "radio",
      clickToSelect: false,

      selected: this.state.selectedAssessmentId
        ? [this.state.selectedAssessmentId]
        : [],
      onSelect: this.handleOnSelect,
      selectionHeaderRenderer: () => <span>Select</span>,
      headerColumnStyle: { textAlign: "center", pointerEvents: 'none'  },
      selectionRenderer: ({ mode, ...rest }: any) => (
        <div style={{ textAlign: "center" }}>
          <input type={mode} {...rest} />
        </div>
      )
    };
    const { selectedAssessmentId, evidenceAssessments } = this.state;
    const sortedEvidenceAssessments = this.getSortedEvidenceAssessments(evidenceAssessments);

    const isLoading =
      this.props.isLoading.getEvidenceAssessments ||
      this.props.isLoading.addAssessmentToDataPeriod;
    const isEmpty =
      evidenceAssessments.length === 0 &&
      !this.props.isLoading.getEvidenceAssessments;

    const classListDesc = (document.getElementsByClassName('caret-4-desc') as HTMLCollectionOf<HTMLElement>)?.[0];
    const classListAsc = (document.getElementsByClassName('caret-4-asc') as HTMLCollectionOf<HTMLElement>)?.[0];
    if(this.state.sortOrder === 'asc') { 
      if(classListAsc){
        classListAsc.style.display = 'inline-block';
      }
    } else if (this.state.sortOrder === 'desc') {
      if(classListDesc){
        classListDesc.style.display = 'inline-block';
      }
    } else if(!this.state.sortField && !this.state.sortOrder) {
      if(classListAsc){
        classListAsc.style.display = 'none';
      }
      if(classListDesc){
        classListDesc.style.display = 'none';
      }
    }

    return (
      <Modal
        animation={false}
        size="xl"
        backdropClassName="customDarkModalBackdrop in"
        show={this.props.showModal}
        onShow={this.handleModalShow}
        onHide={this.handleModalHide}
        centered
        className="teacher-invite-individually-modal"
      >
        <Modal.Header closeButton className="purpleModalHeader">
          <Modal.Title>Add or Create Assessment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-center justify-content-between mb-xl-3 mb-2">
              <h4 className="font-weight-bold">Select an assessment to add to the current data period, or create a new one.</h4>
              {/* <h3 className="font-weight-bold">
                If you need suggestions on assessment please{" "}
                <u>
                  <a
                    className="blueLink"
                    onClick={() => this.setState({ showSuggestionModal: true })}
                  >
                    email us.
                  </a>
                </u>
              </h3> */}
            {/* <div>
              <button
                className="blueBtnMd"
                disabled={isLoading || !selectedAssessmentId}
                onClick={this.handleAddAssessment}
              >
                Select{" "}
                {this.props.isLoading.addAssessmentToDataPeriod && (
                  <Spinner animation="border" size="sm" />
                )}
              </button>
            </div> */}
          </div>
          {/*<br />*/}
          <div className="spaceBetween mb-xl-3 mb-2">
            <InputGroup className="filterSearchDesktop mr-4" style={{width:"300px"}}>
            <InputGroup.Prepend>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
            </InputGroup.Prepend>
            <Form.Control
              type="text"
              placeholder="Search"
              // value={props.studentsFilter}
              onChange={this.handleAssessmentsFilterChange}
              className="filterSearchHeight"
            />
            </InputGroup>
            <OverlayTrigger
              trigger={["hover", "focus"]}
              placement="top"
              overlay={
                <Tooltip className="customTooltip" id= "tooltip-top">
                  Create new custom assessment
                </Tooltip>
              }
            >
              <Button
                bsPrefix="b"
                className="blueBtnSm"
                onClick={() => this.props.openAddAssessmentModal(false, undefined, 'create', 'enterDataManuallyModal')}
              >
                <FontAwesomeIcon
                  icon={faPlusCircle}
                  size="lg"
                  className="mr-1 pointer"
                  style={{color:"#fff"}}
                />
                CREATE ASSESSMENT
              </Button>
            </OverlayTrigger>
          </div>
          {/*<br />*/}
          {isLoading && <LoadingIndicator type="table" rows={10} columns={8} />}
          {!isLoading 
            ?
              <>
              <BootstrapTable
                remote={{ sort: true }}
                wrapperClasses={
                  sortedEvidenceAssessments.length === 0
                    ? "rosterTableEmpty"
                    : "fixedHeightTable sticky-table-header"
                }
                classes="goalTable"
                keyField="id"
                bordered={false}
                bootstrap4
                columns={columns}
                data={sortedEvidenceAssessments.map((evidenceAssessment) => ({
                  id: evidenceAssessment.id,
                  name: evidenceAssessment.name,
                  is_system_assessment: evidenceAssessment.is_system_assessment,
                  use_grade_specific_color_criteria: evidenceAssessment.use_grade_specific_color_criteria,
                  with_reading_algorithm: evidenceAssessment.with_reading_algorithm,
                  dataColumns: evidenceAssessment.measurements
                    .map((measurement) => measurement.display_name)
                    .join(", "),
                  createdBy: getCreatedByLabel(evidenceAssessment),
                  actionsElement: (
                    <div className="d-flex justify-content-center">
                        {evidenceAssessment.owner?.id === this.props.currentUser?.id ? (
                          <OverlayTrigger
                            trigger={["hover", "focus"]}
                            placement="top"
                            overlay={<Tooltip className="customTooltip" id= "tooltip-top">Edit Assessment</Tooltip>}
                          >
                            <FontAwesomeIcon 
                              icon={faPen} 
                              className="pointer" 
                              onClick={() => this.props.openAddAssessmentModal(false, evidenceAssessment, 'edit', 'enterDataManuallyModal')}
                            />
                          </OverlayTrigger>
                        ) : !evidenceAssessment.is_system_assessment && (evidenceAssessment.owner?.id !== this.props.currentUser?.id) ? (
                            <OverlayTrigger
                              trigger={["hover", "focus"]}
                              placement="top"
                              overlay={<Tooltip className="customTooltip" id= "tooltip-top">View Assessment</Tooltip>}
                            >
                              <FontAwesomeIcon 
                                icon={faEye} 
                                className="pointer" 
                                onClick={() => this.props.openAddAssessmentModal(false, evidenceAssessment, 'view', 'enterDataManuallyModal')}
                              />
                            </OverlayTrigger>
                        ) : null}
                  </div>
                  ),
                }))}
                sort={this.state.sortField && this.state.sortOrder ? {
                  dataField: this.state.sortField,
                  order: this.state.sortOrder as SortOrder,
                } : undefined}
                onTableChange={this.handleTableChange}
                noDataIndication={
                  isEmpty ? "No matching assessments" : <LoadingIndicator type="list" rows={10} columns={8} />
                }
                selectRow={selectRow}
              />
              <hr />

              <div className="btnActions">
                <button type="button" className="whiteBtnSm" onClick={this.handleModalHide}>
                  <FormattedMessage id="app.forms.buttons.cancel" />
                </button>
                <button 
                  className="blueBtnSm" 
                  type="submit" 
                  disabled={this.props.isLoading.addAssessmentToDataPeriod || !selectedAssessmentId}
                  onClick={this.handleAddAssessment}
                >
                  <FormattedMessage id="app.forms.buttons.add" />
                  {this.props.isLoading.addAssessmentToDataPeriod && (
                    <Spinner animation="border" size="sm" />
                  )}
                </button>
              </div>
              </>
            : ''
          }          
          {/* <AssessmentSuggestionsModal
            show={this.state.showSuggestionModal}
            onClose={() => this.setState({ showSuggestionModal: false })}
          /> */}
        </Modal.Body>
      </Modal>
    );
  }
}

const mapStateToProps = ({ onboarding, auth }: ApplicationState): PropsFromState => {
  return {
    showModal: onboarding.modalsState.enterDataManuallyModal,
    enterDataManuallyType: onboarding.modalsState.enterDataManuallyType,
    evidenceAssessments: onboarding.evidenceAssessments,
    currentUser: auth.userInfo!,
    isLoading: {
      getEvidenceAssessments: onboarding.isLoading.getEvidenceAssessments,
      addAssessmentToDataPeriod: onboarding.isLoading.addAssessmentToDataPeriod,
      createAssessment: onboarding.isLoading.createAssessment,
      updateAssessment: onboarding.isLoading.updateAssessment,
    },
    errors: {
      getEvidenceAssessments: onboarding.errors.getEvidenceAssessments,
      addAssessmentToDataPeriod: onboarding.errors.addAssessmentToDataPeriod,
      createAssessment: onboarding.errors.createAssessment,
      updateAssessment: onboarding.errors.updateAssessment,
    },
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps =>
  bindActionCreators(
    {
      getEvidenceAssessments: getEvidenceAssessments,
      addAssessmentToDataPeriod: addAssessmentToDataPeriod,
      hideEnterDataManuallyModal: hideEnterDataManuallyModal,
      hidePickDataSourceModal: hidePickDataSourceModal,
      openAddAssessmentModal: openAddAssessmentModal,
      hideAddAssessmentModal: hideAddAssessmentModal,
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EnterDataManuallyModal);
